package info.rezident.app.data.auth

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.analytics.analytics
import dev.gitlive.firebase.auth.EmailAuthProvider
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.auth.externals.GoogleAuthProvider
import dev.gitlive.firebase.auth.externals.getAuth
import dev.gitlive.firebase.auth.externals.getRedirectResult
import dev.gitlive.firebase.auth.externals.signInWithRedirect
import info.rezident.app.core.AppTracking
import info.rezident.app.core.LocalStorage
import info.rezident.app.core.Logger
import info.rezident.app.data.quiz.UserQuizRepository
import info.rezident.app.data.user.UserRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlin.coroutines.cancellation.CancellationException

// LocalStorage keys for redirect flow
private const val PRE_REDIRECT_ANON_UID_KEY = "pre_redirect_anon_uid"

object AuthRepository {
    private val auth: FirebaseAuth = Firebase.auth
    val firebaseUser = auth.authStateChanged

    private val scope = CoroutineScope(Dispatchers.Default)

    init {
        // Simple logging for auth state changes
        firebaseUser.onEach {
            Logger.log("firebaseUser ${it != null}; ${it?.email}")
        }.launchIn(scope)

        // Handle Google redirect results immediately when app starts
        // This is crucial - when Google redirects back to our site, we process it right away
        scope.launch {
            try {
                val redirectResult = handleGoogleRedirectResult()
                Logger.log("Redirect processing completed: ${redirectResult.isSuccess}, processed: ${redirectResult.getOrNull()}")
            } catch (e: Exception) {
                Logger.error("Error processing redirect result on app start: $e")
            }
        }
    }

    /**
     * Initializes authentication system with automatic anonymous authentication.
     *
     * Sets up Firebase Auth state listener to handle the final authenticated state
     * after any redirect processing is complete. Call once during app startup.
     *
     * **Behavior:**
     * - Handles final authenticated state (after redirects are processed)
     * - Creates anonymous users if no user exists
     * - Ensures user documents exist for the final user state
     * - Runs in background using GlobalScope
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    fun initAuth() {
        firebaseUser.mapLatest { user ->
            // Handle the final authenticated state
            if (user == null) {
                // No user exists, create anonymous user
                runCatching {
                    auth.signInAnonymously()
                    Logger.log("Anonymous sign-in successful")
                }.onFailure {
                    if (it is CancellationException) {
                        throw it
                    }
                    Logger.error("Anonymous sign-in failed: $it")
                }
            }

            // Always ensure user document exists for the final user state
            // This happens after any auth state changes (including from redirects)
            UserRepository.initUser(user)
            UserRepository.ensureUserSetAndCached()
        }.launchIn(scope)
    }

    suspend fun getCurrentUser(): FirebaseUser? = firebaseUser
        .filterNotNull()
        .firstOrNull()

    /**
     * Signs in with existing Google account using proper Firebase account linking.
     *
     * For anonymous users: tries linking first, falls back to sign-in + data merge if account exists.
     * For logged-out users: performs regular Firebase authentication.
     *
     * @param idToken The Google ID token for authentication
     * @return [Result] containing success or failure with error details
     */
    suspend fun signInWithGoogle(idToken: String): Result<Unit> {
        return try {
            val currentUser = auth.currentUser
            val credential =
                dev.gitlive.firebase.auth.GoogleAuthProvider
                    .credential(idToken = idToken, accessToken = null)

            when {
                currentUser?.isAnonymous == true -> {
                    // Anonymous user - try linking first, fallback to sign-in + merge
                    val anonymousUid = currentUser.uid
                    try {
                        val linkResult = currentUser.linkWithCredential(credential)
                        linkResult.user?.email?.let { UserRepository.updateUserEmail(it) }
                        Logger.log("Successfully linked anonymous user $anonymousUid with Google")

                        val priceVariant = UserRepository.getUserPriceVariant()
                        AppTracking.logEvent(
                            AppTracking.EVENT_SIGN_UP,
                            mapOf(
                                AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                                AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                                AppTracking.PARAM_PRICE_VARIANT to priceVariant.name,
                            )
                        )
                        Result.success(Unit)
                    } catch (e: Exception) {
                        if (e.message?.contains("auth/account-exists-with-different-credential") == true) {
                            // Account exists - need to sign in and merge data
                            Logger.log("Account exists, preparing to merge anonymous data from $anonymousUid")

                            // Set merge lease on anonymous user document while still authenticated as anonymous
                            UserRepository.setMergeLeaseOnCurrentUser()

                            // Now sign in with the existing account
                            val signInResult = auth.signInWithCredential(credential)
                            val newUid = signInResult.user?.uid

                            // Perform the merge if UIDs are different
                            if (newUid != null && newUid != anonymousUid) {
                                Logger.log("Merging anonymous data from $anonymousUid to existing Google account $newUid")
                                UserRepository.mergeUserData(anonymousUid, newUid)
                            }

                            // Update email after merge (merge overwrites destination doc)
                            signInResult.user?.email?.let { UserRepository.updateUserEmail(it) }

                            val priceVariant = UserRepository.getUserPriceVariant()
                            AppTracking.logEvent(
                                AppTracking.EVENT_LOGIN,
                                mapOf(
                                    AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                                    AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                                    AppTracking.PARAM_PRICE_VARIANT to priceVariant.name
                                )
                            )
                            Result.success(Unit)
                        } else {
                            throw e
                        }
                    }
                }

                currentUser == null -> {
                    // Logged-out user - regular Firebase auth
                    val result = auth.signInWithCredential(credential)
                    val isNewUser = result.additionalUserInfo?.isNewUser == true

                    // Update email in user document
                    result.user?.email?.let { UserRepository.updateUserEmail(it) }

                    val priceVariant = UserRepository.getUserPriceVariant()
                    if (isNewUser) {
                        AppTracking.logEvent(
                            AppTracking.EVENT_SIGN_UP,
                            mapOf(
                                AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                                AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                                AppTracking.PARAM_PRICE_VARIANT to priceVariant.name,
                            )
                        )
                    } else {
                        AppTracking.logEvent(
                            AppTracking.EVENT_LOGIN,
                            mapOf(
                                AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                                AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                                AppTracking.PARAM_PRICE_VARIANT to priceVariant.name
                            )
                        )
                    }
                    Result.success(Unit)
                }

                else -> {
                    // Already authenticated user - shouldn't happen in normal flow
                    Logger.warn("Attempted Google sign-in while already authenticated")
                    Result.success(Unit)
                }
            }
        } catch (e: Exception) {
            Logger.error("Error signing in with Google: $e")
            Result.failure(e)
        }
    }

    /**
     * Signs in with existing email/password account using proper Firebase account linking.
     *
     * For anonymous users: tries linking first, falls back to sign-in + data merge if account exists.
     * For logged-out users: performs regular Firebase authentication.
     *
     * @param email The email address of the existing account
     * @param password The password for the existing account
     * @return [Result] containing success or failure with error details
     */
    suspend fun signInWithEmail(email: String, password: String): Result<Unit> {
        return try {
            val currentUser = auth.currentUser

            when {
                currentUser?.isAnonymous == true -> {
                    // Anonymous user - sign in with existing account and merge data
                    val anonymousUid = currentUser.uid
                    Logger.log("Preparing to merge anonymous data from $anonymousUid")

                    // Set merge lease on anonymous user document while still authenticated as anonymous
                    UserRepository.setMergeLeaseOnCurrentUser()

                    // Now sign in with the existing account
                    val signInResult = auth.signInWithEmailAndPassword(email, password)
                    val newUid = signInResult.user?.uid

                    // Perform the merge if UIDs are different
                    if (newUid != null && newUid != anonymousUid) {
                        Logger.log("Merging anonymous data from $anonymousUid to existing account $newUid")
                        UserRepository.mergeUserData(anonymousUid, newUid)
                    }

                    // Always update email after sign-in (especially after merge which overwrites destination doc)
                    UserRepository.updateUserEmail(email)

                    val priceVariant = UserRepository.getUserPriceVariant()
                    AppTracking.logEvent(
                        AppTracking.EVENT_LOGIN,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_EMAIL,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name
                        )
                    )
                    Result.success(Unit)
                }

                currentUser == null -> {
                    // Logged-out user - regular Firebase auth
                    auth.signInWithEmailAndPassword(email, password)

                    // Update email in user document
                    UserRepository.updateUserEmail(email)

                    val priceVariant = UserRepository.getUserPriceVariant()
                    AppTracking.logEvent(
                        AppTracking.EVENT_LOGIN,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_EMAIL,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name
                        )
                    )
                    Result.success(Unit)
                }

                else -> {
                    // Already authenticated user - shouldn't happen in normal flow
                    Logger.warn("Attempted sign-in while already authenticated")
                    Result.success(Unit)
                }
            }
        } catch (e: Exception) {
            Logger.error("Error signing in with email: $e")
            Result.failure(e)
        }
    }

    /**
     * Creates a new email/password account using proper Firebase account linking.
     *
     * For anonymous users: tries linking (which creates the account and preserves data).
     * For logged-out users: performs regular Firebase registration.
     *
     * @param email The email address for the new account
     * @param password The password for the new account (must meet Firebase requirements)
     * @return [Result] containing success or failure with error details
     */
    suspend fun registerWithEmail(email: String, password: String): Result<Unit> {
        return try {
            val currentUser = auth.currentUser
            val credential = EmailAuthProvider.credential(email, password)

            when {
                currentUser?.isAnonymous == true -> {
                    // Anonymous user - try linking (which creates the account)
                    currentUser.linkWithCredential(credential)
                    UserRepository.updateUserEmail(email)
                    Logger.log("Successfully linked anonymous user ${currentUser.uid} with new email account")

                    val priceVariant = UserRepository.getUserPriceVariant()
                    AppTracking.logEvent(
                        AppTracking.EVENT_SIGN_UP,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_EMAIL,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name,
                        )
                    )
                    Result.success(Unit)
                }

                currentUser == null -> {
                    // Logged-out user - regular Firebase registration
                    auth.createUserWithEmailAndPassword(email, password)
                    UserRepository.updateUserEmail(email)

                    val priceVariant = UserRepository.getUserPriceVariant()
                    AppTracking.logEvent(
                        AppTracking.EVENT_SIGN_UP,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_EMAIL,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name,
                        )
                    )
                    Result.success(Unit)
                }

                else -> {
                    // Already authenticated - shouldn't happen
                    Logger.warn("Attempted registration while already authenticated")
                    Result.failure(IllegalStateException("User is already authenticated"))
                }
            }
        } catch (e: Exception) {
            Logger.error("Error registering with email: $e")
            Result.failure(e)
        }
    }

    /**
     * Initiates Google Sign-In using Firebase Auth redirect flow.
     * Sets merge lease and stores anonymous UID if present for later data merge.
     */
    suspend fun initiateGoogleSignInRedirect(): Result<Unit> {
        return try {
            // Store anonymous UID and set merge lease for merge after redirect if current user is anonymous
            val currentUser = auth.currentUser
            if (currentUser?.isAnonymous == true) {
                LocalStorage.save(PRE_REDIRECT_ANON_UID_KEY, currentUser.uid)
                Logger.log("Stored pre-redirect anonymous UID: ${currentUser.uid}")

                // Set merge lease on anonymous user document before redirect
                UserRepository.setMergeLeaseOnCurrentUser()
                Logger.log("Merge lease set before redirect")
            }

            val provider = GoogleAuthProvider().apply {
                addScope("email")
                addScope("profile")
            }

            signInWithRedirect(getAuth(), provider)
            Logger.log("Google sign-in redirect initiated")
            Result.success(Unit)
        } catch (e: Exception) {
            Logger.error("Error initiating Google redirect: $e")
            Result.failure(e)
        }
    }

    /**
     * Handles Google redirect result directly without double sign-in.
     * Call this on app startup to check for completed redirects.
     *
     * @return Result<Boolean> - true if redirect was processed, false if no redirect result
     */
    private suspend fun handleGoogleRedirectResult(): Result<Boolean> {
        return try {
            val result = getRedirectResult(getAuth())

            val authResult = result.await()
            val user = authResult?.user
            Logger.log("Google redirect result: $user")

            if (user != null) {
                Logger.log("Processing Google redirect result for user: ${user.email}")

                // Check if we need to merge anonymous data
                val preRedirectAnonUid = LocalStorage.get(PRE_REDIRECT_ANON_UID_KEY)
                if (preRedirectAnonUid != null && preRedirectAnonUid != user.uid) {
                    Logger.log("Merging anonymous data from $preRedirectAnonUid to Google account ${user.uid}")
                    UserRepository.mergeUserData(preRedirectAnonUid, user.uid)
                    LocalStorage.remove(PRE_REDIRECT_ANON_UID_KEY)
                }

                // Update email in user document (especially important after merge)
                user.email?.let { UserRepository.updateUserEmail(it) }

                // Log appropriate analytics event
                val isNewUser = authResult.user.metadata.run {
                    creationTime == lastSignInTime
                }
                val priceVariant = UserRepository.getUserPriceVariant()

                if (isNewUser) {
                    AppTracking.logEvent(
                        AppTracking.EVENT_SIGN_UP,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name,
                        )
                    )
                } else {
                    AppTracking.logEvent(
                        AppTracking.EVENT_LOGIN,
                        mapOf(
                            AppTracking.PARAM_METHOD to AppTracking.METHOD_GOOGLE,
                            AppTracking.PARAM_ACADEMIC_YEAR to (LocalStorage.get("academic_year") ?: "Unknown"),
                            AppTracking.PARAM_PRICE_VARIANT to priceVariant.name
                        )
                    )
                }

                Logger.log("Successfully processed Google redirect authentication")
                Result.success(true) // Redirect was processed
            } else {
                // No redirect result (normal page load)
                Result.success(false) // No redirect to process
            }
        } catch (e: Exception) {
            Logger.error("Error handling Google redirect result: $e")
            Result.failure(e)
        }
    }

    suspend fun logOut() {
        auth.signOut()
        LocalStorage.clear()
        UserQuizRepository.clearCachedQuizzes()
        Firebase.analytics.logEvent("user_log_out")
    }
}